import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Heart, Star, Plus, X, Camera, Calendar,
  PartyPopper, Baby, Home, Cake, Gift, Send, Share2,
  Sparkles, MessageCircle, Trophy, PawPrint
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Celebration Types
const CELEBRATION_TYPES = [
  { id: 'litter', name: 'New Litter', icon: '🍼', description: 'Announce new puppies born' },
  { id: 'welcome', name: 'Welcome Home', icon: '🏠', description: 'Celebrating a new family member' },
  { id: 'birthday', name: 'Birthday', icon: '🎂', description: 'Another year of tail wags' },
  { id: 'milestone', name: 'Milestone', icon: '🏆', description: 'First walk, graduation, etc.' },
  { id: 'adoption', name: 'Adoption Day', icon: '❤️', description: 'Anniversary of finding each other' },
  { id: 'gotcha', name: 'Gotcha Day', icon: '🎉', description: 'The day they became family' },
];

// Virtual Gifts for celebrations
const CELEBRATION_GIFTS = [
  { id: 'balloon', name: 'Balloons', emoji: '🎈' },
  { id: 'cake', name: 'Pupcake', emoji: '🎂' },
  { id: 'toy', name: 'New Toy', emoji: '🧸' },
  { id: 'treat', name: 'Treats', emoji: '🦴' },
  { id: 'heart', name: 'Love', emoji: '❤️' },
  { id: 'party', name: 'Party', emoji: '🎉' },
];

// Floating confetti animation
const FloatingConfetti = ({ emoji, delay = 0 }) => (
  <motion.div
    className="absolute text-2xl pointer-events-none"
    initial={{ opacity: 0, y: -20, x: Math.random() * 200 - 100, rotate: 0 }}
    animate={{ 
      opacity: [0, 1, 1, 0],
      y: [0, 300],
      x: [Math.random() * 200 - 100, Math.random() * 200 - 100],
      rotate: [0, 360]
    }}
    transition={{ 
      duration: 3,
      delay,
      repeat: Infinity,
      repeatDelay: Math.random() * 3
    }}
  >
    {emoji}
  </motion.div>
);

// Celebration Card Component
const CelebrationCard = ({ celebration, onClick }) => {
  const typeConfig = CELEBRATION_TYPES.find(t => t.id === celebration.type) || CELEBRATION_TYPES[0];
  const isToday = new Date(celebration.date).toDateString() === new Date().toDateString();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5, scale: 1.02 }}
      onClick={onClick}
      className="bg-white rounded-3xl overflow-hidden shadow-sm border border-[#EBE8E0] cursor-pointer group"
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        {celebration.photo ? (
          <img 
            src={celebration.photo} 
            alt={celebration.pet_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#FF7555]/20 to-[#F4D35E]/20 flex items-center justify-center">
            <span className="text-6xl">{typeConfig.icon}</span>
          </div>
        )}
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Type Badge */}
        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
          <span>{typeConfig.icon}</span>
          <span className="text-[#1A1817]">{typeConfig.name}</span>
        </div>

        {/* Today Badge */}
        {isToday && (
          <div className="absolute top-3 right-3 bg-[#FF7555] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
            <Sparkles className="w-3 h-3" />
            Today!
          </div>
        )}
        
        {/* Name */}
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="text-white text-xl font-bold" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            {celebration.pet_name}
          </h3>
          <p className="text-white/80 text-sm">{celebration.title}</p>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <p className="text-sm text-[#7A7571] mb-3 line-clamp-2">
          {celebration.message || 'Celebrating this special moment! 🎉'}
        </p>
        
        <div className="flex items-center justify-between text-xs text-[#7A7571]">
          <span>
            {new Date(celebration.date).toLocaleDateString()}
          </span>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              🎈 {celebration.gifts?.balloon || 0}
            </span>
            <span className="flex items-center gap-1">
              ❤️ {celebration.gifts?.heart || 0}
            </span>
            <span className="flex items-center gap-1">
              💬 {celebration.messages?.length || 0}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Create Celebration Modal
const CreateCelebrationModal = ({ isOpen, onClose, onSave }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    type: '',
    pet_name: '',
    title: '',
    date: new Date().toISOString().split('T')[0],
    photo: '',
    message: '',
    litter_count: ''
  });
  const [uploading, setUploading] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const sigRes = await fetch(`${API_URL}/api/puppoto/cloudinary/signature?folder=puppoto/celebrations`, {
        credentials: 'include'
      });
      
      if (sigRes.ok) {
        const sig = await sigRes.json();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('api_key', sig.api_key);
        formData.append('timestamp', sig.timestamp);
        formData.append('signature', sig.signature);
        formData.append('folder', sig.folder);

        const uploadRes = await fetch(
          `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
          { method: 'POST', body: formData }
        );

        if (uploadRes.ok) {
          const data = await uploadRes.json();
          setForm(p => ({ ...p, photo: data.secure_url }));
          toast.success('Photo uploaded! 📸');
        }
      } else {
        // Fallback to local preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setForm(p => ({ ...p, photo: reader.result }));
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = () => {
    if (!form.type || !form.pet_name) {
      toast.error('Please fill in the required fields');
      return;
    }
    onSave(form);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              🎉 New Celebration
            </h2>
            <p className="text-sm text-[#7A7571]">Share the joy with the community!</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="flex gap-2 mb-6">
          {[1, 2].map(s => (
            <div 
              key={s}
              className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-[#FF7555]' : 'bg-[#EBE8E0]'}`}
            />
          ))}
        </div>

        {/* Step 1: Type & Basic Info */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Celebration Type */}
            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-3">What are we celebrating? *</label>
              <div className="grid grid-cols-2 gap-3">
                {CELEBRATION_TYPES.map(type => (
                  <button
                    key={type.id}
                    onClick={() => setForm(p => ({ ...p, type: type.id }))}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      form.type === type.id 
                        ? 'border-[#FF7555] bg-[#FF7555]/5' 
                        : 'border-[#EBE8E0] hover:border-[#FF7555]/50'
                    }`}
                  >
                    <span className="text-2xl mb-1 block">{type.icon}</span>
                    <span className="font-medium text-sm text-[#1A1817]">{type.name}</span>
                    <span className="text-xs text-[#7A7571] block">{type.description}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">Pet's Name *</label>
              <Input
                value={form.pet_name}
                onChange={e => setForm(p => ({ ...p, pet_name: e.target.value }))}
                placeholder="Who are we celebrating?"
                className="rounded-xl"
              />
            </div>

            {form.type === 'litter' && (
              <div>
                <label className="block text-sm font-medium text-[#1A1817] mb-1">How many puppies?</label>
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={form.litter_count}
                  onChange={e => setForm(p => ({ ...p, litter_count: e.target.value }))}
                  placeholder="e.g., 6"
                  className="rounded-xl"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">
                {form.type === 'birthday' ? 'Birthday' : form.type === 'litter' ? 'Born on' : 'Date'}
              </label>
              <Input
                type="date"
                value={form.date}
                onChange={e => setForm(p => ({ ...p, date: e.target.value }))}
                className="rounded-xl"
              />
            </div>

            <Button
              onClick={() => setStep(2)}
              disabled={!form.type || !form.pet_name}
              className="w-full h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
            >
              Continue
            </Button>
          </div>
        )}

        {/* Step 2: Photo & Message */}
        {step === 2 && (
          <div className="space-y-4">
            {/* Photo Upload */}
            <div className="text-center">
              <div 
                className="w-full h-48 mx-auto rounded-2xl overflow-hidden bg-gradient-to-br from-[#FF7555]/10 to-[#F4D35E]/10 flex items-center justify-center cursor-pointer border-2 border-dashed border-[#EBE8E0] hover:border-[#FF7555] transition-colors"
                onClick={() => document.getElementById('celebration-photo').click()}
              >
                {form.photo ? (
                  <img src={form.photo} alt="Celebration" className="w-full h-full object-cover" />
                ) : uploading ? (
                  <div className="animate-pulse text-4xl">📷</div>
                ) : (
                  <div className="text-center">
                    <Camera className="w-10 h-10 text-[#FF7555] mx-auto mb-2" />
                    <span className="text-sm text-[#7A7571]">Add a photo!</span>
                  </div>
                )}
              </div>
              <input
                id="celebration-photo"
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">Title</label>
              <Input
                value={form.title}
                onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder={
                  form.type === 'birthday' ? `${form.pet_name}'s Birthday!` :
                  form.type === 'litter' ? `${form.litter_count || 'New'} puppies born!` :
                  form.type === 'welcome' ? `Welcome home, ${form.pet_name}!` :
                  'Celebration title...'
                }
                className="rounded-xl"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1A1817] mb-1">Message</label>
              <textarea
                value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                placeholder="Share the joy! Tell us about this special moment..."
                className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-24"
              />
            </div>

            <div className="bg-[#FF7555]/10 rounded-xl p-4 text-center">
              <span className="text-4xl mb-2 block">🎉</span>
              <p className="text-[#FF7555] font-medium">
                Let's celebrate {form.pet_name}!
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 h-12 rounded-xl"
              >
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1 h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
              >
                🎉 Share Celebration!
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Send Gift Modal
const SendGiftModal = ({ isOpen, onClose, onSend, petName }) => {
  const [selectedGift, setSelectedGift] = useState(null);
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <h3 className="text-xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
          🎁 Send a Gift to {petName}!
        </h3>

        <div className="grid grid-cols-3 gap-3 mb-4">
          {CELEBRATION_GIFTS.map(gift => (
            <button
              key={gift.id}
              onClick={() => setSelectedGift(gift)}
              className={`p-4 rounded-xl border-2 transition-all ${
                selectedGift?.id === gift.id 
                  ? 'border-[#FF7555] bg-[#FF7555]/10' 
                  : 'border-[#EBE8E0] hover:border-[#FF7555]/50'
              }`}
            >
              <span className="text-3xl block mb-1">{gift.emoji}</span>
              <span className="text-xs text-[#7A7571]">{gift.name}</span>
            </button>
          ))}
        </div>

        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="Add a congratulations message!"
          className="w-full px-4 py-3 rounded-xl border border-[#EBE8E0] resize-none h-20 mb-4"
        />

        <Button
          onClick={() => { onSend(selectedGift, message); setSelectedGift(null); setMessage(''); }}
          disabled={!selectedGift}
          className="w-full h-12 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
        >
          <Send className="w-4 h-4 mr-2" />
          Send {selectedGift?.name || 'Gift'}! 🎉
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Main Celebrations Component
const PupCelebrations = () => {
  const navigate = useNavigate();
  const [celebrations, setCelebrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCelebration, setSelectedCelebration] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchCelebrations();
  }, []);

  const fetchCelebrations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/puppoto/celebrations`);
      if (response.ok) {
        const data = await response.json();
        setCelebrations(data.celebrations || []);
      }
    } catch (error) {
      console.error('Failed to fetch celebrations:', error);
      // Demo data
      setCelebrations([
        {
          id: '1',
          type: 'litter',
          pet_name: 'Bella',
          title: '6 beautiful puppies born!',
          photo: 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=400',
          date: '2026-03-20',
          message: 'Bella gave birth to 6 healthy Golden Retriever puppies! 4 boys and 2 girls. Mother and babies doing wonderfully!',
          litter_count: 6,
          gifts: { balloon: 23, heart: 45, cake: 12 },
          messages: [
            { author: 'DogLover123', text: 'Congratulations! They are adorable! 🐕', date: '2026-03-20' }
          ]
        },
        {
          id: '2',
          type: 'welcome',
          pet_name: 'Max',
          title: 'Welcome home, Max!',
          photo: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400',
          date: '2026-03-18',
          message: 'After months of waiting, we finally brought Max home! He\'s already made himself comfortable on the couch.',
          gifts: { balloon: 18, heart: 32, toy: 8 },
          messages: []
        },
        {
          id: '3',
          type: 'birthday',
          pet_name: 'Luna',
          title: 'Luna turns 3! 🎂',
          photo: 'https://images.unsplash.com/photo-1530281700549-e82e7bf110d6?w=400',
          date: '2026-03-15',
          message: 'Happy 3rd birthday to our beautiful Luna! She got a special pupcake and new toys!',
          gifts: { balloon: 34, cake: 28, heart: 56 },
          messages: []
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCelebration = async (formData) => {
    try {
      const response = await fetch(`${API_URL}/api/puppoto/celebrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('🎉 Celebration shared! Congrats on ' + formData.pet_name + '!');
        fetchCelebrations();
      } else {
        // Add locally for demo
        setCelebrations(prev => [{
          ...formData,
          id: Date.now().toString(),
          gifts: {},
          messages: []
        }, ...prev]);
        toast.success('🎉 Celebration shared! Congrats on ' + formData.pet_name + '!');
      }
    } catch (error) {
      setCelebrations(prev => [{
        ...formData,
        id: Date.now().toString(),
        gifts: {},
        messages: []
      }, ...prev]);
      toast.success('🎉 Celebration shared! Congrats on ' + formData.pet_name + '!');
    }
    setShowCreateModal(false);
  };

  const handleSendGift = (gift, message) => {
    if (!selectedCelebration || !gift) return;

    setCelebrations(prev => prev.map(c => {
      if (c.id === selectedCelebration.id) {
        return {
          ...c,
          gifts: {
            ...c.gifts,
            [gift.id]: (c.gifts?.[gift.id] || 0) + 1
          },
          messages: message ? [...(c.messages || []), {
            author: 'Anonymous',
            text: message,
            gift: gift.emoji,
            date: new Date().toISOString()
          }] : c.messages
        };
      }
      return c;
    }));

    toast.success(`${gift.emoji} sent to ${selectedCelebration.pet_name}! 🎉`);
    setShowGiftModal(false);
  };

  const filteredCelebrations = filter === 'all' 
    ? celebrations 
    : celebrations.filter(c => c.type === filter);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FF7555]/5 via-white to-[#F4D35E]/5">
      {/* Floating confetti */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {['🎈', '🎉', '⭐', '🎊', '💖'].map((emoji, i) => (
          <FloatingConfetti key={i} emoji={emoji} delay={i * 0.5} />
        ))}
      </div>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#EBE8E0] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/puppoto')}
            className="flex items-center gap-2 text-[#1A1817] hover:text-[#FF7555] transition"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">Back to Puppoto</span>
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-2xl">🎉</span>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Pup Celebrations
            </span>
          </div>
          
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
          >
            <Plus className="w-4 h-4 mr-2" />
            Share Celebration
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4 text-center relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          <span className="text-6xl mb-6 block">🎉</span>
          
          <h1 className="text-4xl md:text-5xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Pup Celebrations
          </h1>
          
          <p className="text-lg text-[#7A7571] mb-4 leading-relaxed">
            Every new life, every gotcha day, every birthday is worth celebrating! 
            Share your joy with the Puppoto community.
          </p>

          <p className="text-[#FF7555] font-medium">
            🐕 New puppies • 🏠 Welcome home • 🎂 Birthdays • 🏆 Milestones
          </p>
        </motion.div>
      </section>

      {/* Filters */}
      <section className="px-4 mb-8">
        <div className="max-w-6xl mx-auto flex flex-wrap justify-center gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filter === 'all' ? 'bg-[#FF7555] text-white' : 'bg-white text-[#7A7571] hover:bg-[#F5F3EE]'
            }`}
          >
            All Celebrations
          </button>
          {CELEBRATION_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => setFilter(type.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${
                filter === type.id ? 'bg-[#FF7555] text-white' : 'bg-white text-[#7A7571] hover:bg-[#F5F3EE]'
              }`}
            >
              <span>{type.icon}</span>
              {type.name}
            </button>
          ))}
        </div>
      </section>

      {/* Celebrations Grid */}
      <section className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-12 text-[#7A7571]">Loading celebrations...</div>
          ) : filteredCelebrations.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">🎈</span>
              <h3 className="text-xl font-bold text-[#1A1817] mb-2">No celebrations yet</h3>
              <p className="text-[#7A7571] mb-4">Be the first to share some joy!</p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
              >
                <Plus className="w-4 h-4 mr-2" />
                Share Celebration
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCelebrations.map(celebration => (
                <CelebrationCard
                  key={celebration.id}
                  celebration={celebration}
                  onClick={() => { setSelectedCelebration(celebration); setShowGiftModal(true); }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#EBE8E0] bg-white/50">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-sm text-[#7A7571]">
            🎉 Pup Celebrations • A Puppoto Happy Place • Share the joy!
          </p>
        </div>
      </footer>

      {/* Modals */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateCelebrationModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSave={handleCreateCelebration}
          />
        )}
        {showGiftModal && selectedCelebration && (
          <SendGiftModal
            isOpen={showGiftModal}
            onClose={() => { setShowGiftModal(false); setSelectedCelebration(null); }}
            onSend={handleSendGift}
            petName={selectedCelebration.pet_name}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PupCelebrations;
